var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import _defaultsDeep from "lodash/defaultsDeep";
import { CUJStatus } from "@pns-fe/monitor";
import { CUJEvents } from "./constants/cujEvents";
import { Mode, Region } from "./constants/env";
import { LifeCycleEventName, StorageType } from "./constants/sdk";
import { BaseInfoModule } from "./modules/info-module";
import LifeCycleModule from "./modules/life-cycle-module";
import LoggingModule from "./modules/logging-module";
import ServiceModule from "./modules/service-module";
import StorageModule from "./modules/storage-module";
const DEFAULT_CONFIG = {
  appId: -1,
  domain: "",
  entityKeys: [],
  mode: Mode.PRODUCTION,
  isPPE: false,
  debug: false,
  fetchDataAtInit: true,
  region: Region.SINGAPORE_CENTRAL,
  storageType: StorageType.sessionStorage,
  modules: {
    info: new BaseInfoModule()
  }
};
class ConsentClientFrameworkSDK {
  constructor() {
    this._isInitialized = false;
    this._validateInitStatus = () => {
      if (!this._isInitialized) {
        console.error("[SDK]: SDK is used before init");
        return false;
      }
      return true;
    };
    this.getConsentRecord = (entityKey) => {
      if (!this._validateInitStatus()) {
        return null;
      }
      LoggingModule.reportCUJ(CUJEvents.GET_RECORD, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: getConsentRecord", { entityKey });
        const data = LifeCycleModule.getConsentRecord(entityKey);
        LoggingModule.reportCUJ(CUJEvents.GET_RECORD, CUJStatus.FINISH);
        return data;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.GET_RECORD, CUJStatus.FAILURE, {
          error: e
        });
        return null;
      }
    };
    this.getConsentRecords = (entityKeys) => {
      if (!this._validateInitStatus()) {
        return [];
      }
      LoggingModule.reportCUJ(CUJEvents.GET_RECORDS, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: getConsentRecords", { entityKeys });
        const data = LifeCycleModule.getConsentRecords(entityKeys);
        LoggingModule.reportCUJ(CUJEvents.GET_RECORDS, CUJStatus.FINISH);
        return data;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.GET_RECORDS, CUJStatus.FAILURE, {
          error: e
        });
        return [];
      }
    };
    this.getConsentEntity = (entityKey) => {
      if (!this._validateInitStatus()) {
        return null;
      }
      LoggingModule.reportCUJ(CUJEvents.GET_ENTITY, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: getConsentEntity", { entityKey });
        const data = LifeCycleModule.getConsentEntity(entityKey);
        LoggingModule.reportCUJ(CUJEvents.GET_ENTITY, CUJStatus.FINISH);
        return data;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.GET_ENTITY, CUJStatus.FAILURE, {
          error: e
        });
        return null;
      }
    };
    this.getConsentEntities = (entityKeys) => {
      if (!this._validateInitStatus()) {
        return [];
      }
      LoggingModule.reportCUJ(CUJEvents.GET_ENTITIES, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: getConsentEntities", { entityKeys });
        const data = LifeCycleModule.getConsentEntities(entityKeys);
        LoggingModule.reportCUJ(CUJEvents.GET_ENTITIES, CUJStatus.FINISH);
        return data;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.GET_ENTITIES, CUJStatus.FAILURE, {
          error: e
        });
        return [];
      }
    };
    this.updateConsentRecords = (params) => __async(this, null, function* () {
      if (!this._validateInitStatus()) {
        return Promise.resolve();
      }
      LoggingModule.reportCUJ(CUJEvents.UPDATE_RECORD, CUJStatus.START, {
        data: params
      });
      try {
        LoggingModule.info("[SDK]: updateConsentRecords", { params });
        const data = yield LifeCycleModule.updateConsentRecords(params);
        LoggingModule.reportCUJ(CUJEvents.UPDATE_RECORD, CUJStatus.FINISH, {
          data: data ? Object.keys(data).map((key) => {
            var _a, _b;
            return {
              key,
              status: (_b = (_a = data[key]) == null ? void 0 : _a.record) == null ? void 0 : _b.status
            };
          }) : []
        });
        return data;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.GET_ENTITIES, CUJStatus.FAILURE, {
          error: e
        });
        throw e;
      }
    });
    this.removeNotification = (arg) => {
      if (!this._validateInitStatus()) {
        return;
      }
      LoggingModule.info("[SDK]: removeNotification", { arg });
      LifeCycleModule.removeNotification(arg);
    };
    this.notify = (arg) => {
      if (!this._validateInitStatus()) {
        return;
      }
      LoggingModule.info("[SDK]: notify", { arg });
      LifeCycleModule.notify(arg);
    };
    this.fetch = (arg) => __async(this, null, function* () {
      if (!this._validateInitStatus()) {
        return Promise.resolve();
      }
      LoggingModule.reportCUJ(CUJEvents.FETCH_DATA, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: fetch", { arg });
        const res = yield LifeCycleModule.fetch(arg);
        LoggingModule.reportCUJ(CUJEvents.FETCH_DATA, CUJStatus.FINISH);
        return res;
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.FETCH_DATA, CUJStatus.FAILURE, {
          error: e
        });
        throw e;
      }
    });
    this.refresh = (arg) => {
      if (!this._validateInitStatus()) {
        return Promise.resolve();
      }
      LoggingModule.info("[SDK]: refresh", { arg });
      return LifeCycleModule.fetch(arg);
    };
    this.addEventListener = (eventName, id, callback) => {
      if (!this._validateInitStatus()) {
        return;
      }
      LoggingModule.reportCUJ(CUJEvents.SUBSCRIBE_EVENT, CUJStatus.START);
      LoggingModule.info("[SDK]: addEventListener");
      if (!eventName || !LifeCycleEventName[eventName] || !id || typeof callback !== "function") {
        const errorMsg = "[SDK]: addEventListener - invalid param for adding new event listener";
        const error = new Error(errorMsg);
        LoggingModule.error(errorMsg, error);
        LoggingModule.reportCUJ(CUJEvents.SUBSCRIBE_EVENT, CUJStatus.FAILURE, {
          error
        });
        return;
      }
      try {
        LifeCycleModule.addEventListener(eventName, id, callback);
        LoggingModule.reportCUJ(CUJEvents.SUBSCRIBE_EVENT, CUJStatus.FINISH);
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.SUBSCRIBE_EVENT, CUJStatus.FAILURE, {
          error: e
        });
      }
    };
    this.removeEventListener = (eventName, id) => {
      if (!this._validateInitStatus()) {
        return;
      }
      LoggingModule.reportCUJ(CUJEvents.UNSUBSCRIBE_EVENT, CUJStatus.START);
      LoggingModule.info("[SDK]: removeEventListener");
      try {
        LifeCycleModule.removeEventListener(eventName, id);
        LoggingModule.reportCUJ(CUJEvents.UNSUBSCRIBE_EVENT, CUJStatus.FINISH);
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.UNSUBSCRIBE_EVENT, CUJStatus.FAILURE, {
          error: e
        });
      }
    };
    this.destruct = () => {
      LoggingModule.info("[SDK]: destruct");
      LifeCycleModule.destruct();
      ServiceModule.destruct();
      StorageModule.destruct();
      LoggingModule.destruct();
      this._isInitialized = false;
    };
    if (!ConsentClientFrameworkSDK._instance) {
      ConsentClientFrameworkSDK._instance = this;
    }
    return ConsentClientFrameworkSDK._instance;
  }
  init(config) {
    return __async(this, null, function* () {
      if (typeof window === "undefined") {
        console.warn("ConsentClientFrameworkSDK can not be init on server");
        return Promise.resolve();
      }
      if (this._isInitialized) {
        throw new Error("ConsentClientFrameworkSDK has been initialized");
      }
      const sdkConfig = _defaultsDeep({}, config, DEFAULT_CONFIG);
      const { debug, fetchDataAtInit } = sdkConfig;
      if (debug) {
        window.consentSDK = this;
        window.consentSDK.storage = StorageModule;
      }
      LoggingModule.init(sdkConfig);
      LoggingModule.reportCUJ(CUJEvents.SDK_INIT, CUJStatus.START);
      try {
        LoggingModule.info("[SDK]: init global module");
        ServiceModule.init(sdkConfig);
        StorageModule.init(sdkConfig);
        LoggingModule.info("[SDK]: init life cycle");
        LifeCycleModule.init(sdkConfig);
        this._isInitialized = true;
        if (fetchDataAtInit) {
          LoggingModule.reportCUJ(CUJEvents.FETCH_DATA, CUJStatus.START);
          const data = yield LifeCycleModule.fetch();
          LoggingModule.reportCUJ(CUJEvents.FETCH_DATA, CUJStatus.FINISH);
          LoggingModule.reportCUJ(CUJEvents.SDK_INIT, CUJStatus.FINISH);
          return data;
        }
        LoggingModule.reportCUJ(CUJEvents.SDK_INIT, CUJStatus.FINISH);
        return Promise.resolve();
      } catch (e) {
        LoggingModule.reportCUJ(CUJEvents.SDK_INIT, CUJStatus.FAILURE, {
          error: e
        });
        throw e;
      }
    });
  }
  isInitialized() {
    LoggingModule.info("[SDK]: isInitialized");
    return this._isInitialized;
  }
}
const instance = new ConsentClientFrameworkSDK();
var sdk_default = instance;
export {
  sdk_default as default
};
